// THREE top-level Client Portal sections: SERVICE HISTORY and CALL-OUTS (Session AFH), and since
// Session AMX REPAIRS AND WORKS. Each is a cross-site feed of the customer's completed visits (most
// recent first, across all their sites). They are SEPARATE sections, never one merged "works
// history" (the Director's structure). A clean service visit carries the "No faults found" verdict;
// a call-out and a repair never carry a verdict, and NO fault detail crosses (the server sends only
// the boolean-shaped outcome). NO pricing/contract value, engineer names or internal notes. A
// lapsed session (401) sends the customer back to sign in.
//
// Session AMX: a row is no longer an inert log line. Where the visit's report is finished, the row
// carries a View control that opens the SAME PDF the office downloads, in the existing portal
// viewer. Where it is not, the row says so plainly rather than offering a dead link.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { PortalHistoryFeedRow } from "../../lib/api";
import { PortalShell } from "./PortalShell";
import { ShowAllList } from "./ShowAllList";
import { PortalViewDocumentButton } from "./PortalDocumentViewer";
import { FullScreenLoader } from "../../components/Loader";
import { ContactValue } from "../../components/ContactValue";

// Which proxy URL opens this row's report. The row's own reportSource decides, so the two document
// families (a job's completion certificate, a call-out's filed attendance report) can never be
// served from the wrong route. Returns null when the row carries neither id, which is what
// reportAvailable already tells the caller.
export function feedRowReportHref(row: PortalHistoryFeedRow): string | null {
  if (row.reportSource === "callout") return row.calloutId ? api.portal.calloutReportHref(row.calloutId) : null;
  return row.jobId ? api.portal.jobReportHref(row.jobId) : null;
}

// What the viewer's header and its screen-reader label call this document. Plain English, no
// reference codes: the customer knows this row by where and when, which is what the row shows.
export function feedRowReportTitle(kind: FeedKind, row: PortalHistoryFeedRow): string {
  const noun = row.reportLabel ?? (kind === "service" ? "Service record" : kind === "callouts" ? "Call-out report" : "Repair report");
  return `${noun}, ${row.liftRef}, ${row.dateDisplay}`;
}

// The feed shows at most this many rows (the server caps each section at SITE_HISTORY_SECTION_CAP).
// At the cap the subtitle's "most recent first" is honest but the list is not the full history, so a
// quiet note says so (Session AFH fix: the old "Every ... across your sites" over-claimed totality).
const FEED_CAP = 50;

// The two top-level history pages are thin wrappers over one shared feed view.
export function PortalHistoryPage() {
  return (
    <HistoryFeed
      kind="service"
      title="Service history"
      subtitle="Your completed service visits across your sites, most recent first."
      emptyText="No completed service visits are recorded yet."
    />
  );
}

export function PortalCalloutsPage() {
  return (
    <HistoryFeed
      kind="callouts"
      title="Call-out history"
      subtitle="Your recorded call-outs across your sites, most recent first."
      emptyText="No call-outs are recorded."
    />
  );
}

// Session AMX: the third top-level section. Repairs were reachable only by opening a site first
// (the per-site Repairs and Works tab, Session AFH); the Director asked for them to have their own
// place. Completed PROJECT works stay on the site page: they are a different document family with
// no report to open here, and merging them into a cross-site feed would put rows on this page that
// behave differently from every other row on it.
export function PortalRepairsPage() {
  return (
    <HistoryFeed
      kind="repairs"
      title="Repairs and works"
      subtitle="Your completed repair and technician visits across your sites, most recent first."
      emptyText="No repairs are recorded."
    />
  );
}

type FeedKind = "service" | "callouts" | "repairs";

function HistoryFeed({
  kind,
  title,
  subtitle,
  emptyText,
}: {
  kind: FeedKind;
  title: string;
  subtitle: string;
  emptyText: string;
}) {
  const navigate = useNavigate();
  const [userName, setUserName] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<PortalHistoryFeedRow[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    Promise.all([api.portal.me(), api.portal.history()])
      .then(([me, history]) => {
        if (!live) return;
        setUserName(me.customer.name);
        setRows(kind === "service" ? history.serviceVisits : kind === "callouts" ? history.callouts : history.repairs);
      })
      .catch((err) => {
        if (!live) return;
        if (err instanceof ApiError && err.status === 401) navigate("/portal/login", { replace: true });
        else setError(true);
      });
    return () => {
      live = false;
    };
  }, [navigate, kind]);

  async function logout() {
    try {
      await api.portal.logout();
    } finally {
      navigate("/portal/login", { replace: true });
    }
  }

  if (error) {
    return (
      <PortalShell>
        <div className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-6 text-center">
          <h1 className="mb-2 font-heading text-2xl text-ele-white">Something went wrong</h1>
          <p className="text-ele-body">
            Please try again in a moment, or call the office on{" "}
            <ContactValue value="0203 576 4760" kind="phone" className="underline" />.
          </p>
        </div>
      </PortalShell>
    );
  }
  if (!rows) return <FullScreenLoader />;

  return (
    <PortalShell wide userName={userName} onSignOut={logout}>
      <header className="mb-6">
        <h1 className="font-heading text-3xl tracking-wide text-ele-white">{title}</h1>
        <p className="mt-1 text-ele-body">{subtitle}</p>
      </header>

      {rows.length === 0 ? (
        <p className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 text-sm text-ele-body">{emptyText}</p>
      ) : (
        <>
          <ShowAllList
            count={rows.length}
            render={(cap) => (
              <ul className="overflow-hidden rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)]">
                {rows.slice(0, cap).map((r, i) => {
                  const href = r.reportAvailable ? feedRowReportHref(r) : null;
                  return (
                    <li key={i} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 last:border-0">
                      <div>
                        <p className="text-sm text-ele-white">{r.siteName}</p>
                        <p className="text-xs text-ele-body">{r.liftRef}</p>
                      </div>
                      <span className="flex items-center gap-3 text-xs text-ele-body">
                        {r.outcome && <span className="text-ele-green">{r.outcome}</span>}
                        {r.dateDisplay}
                        {/* The report opens in the existing portal viewer (Session AGH), which
                            already handles focus, Escape, the inert page behind and the iOS Safari
                            fallbacks. A visit whose report is not finished says so rather than
                            offering a link that would 404. */}
                        {href ? (
                          <PortalViewDocumentButton href={href} title={feedRowReportTitle(kind, r)} />
                        ) : (
                          <span className="text-ele-body/70">Report being finalised.</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          />
          {rows.length >= FEED_CAP && <p className="mt-3 text-xs text-ele-body">Showing the 50 most recent.</p>}
        </>
      )}
    </PortalShell>
  );
}
